import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Home, 
  ShoppingBag, 
  Heart, 
  Bell, 
  ShoppingCart, 
  LogOut, 
  Receipt, 
  Settings, 
  Store, 
  Radar, 
  HelpCircle,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../Logo';
import NotificationBell from './NotificationBell';
import OrderNotificationBell from './OrderNotificationBell';
import EmailVerificationBanner from './EmailVerificationBanner';
import LanguageSwitcher from '../LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import ConsumerWalkthrough, { useConsumerWalkthrough } from './ConsumerWalkthrough';

export default function ConsumerLayout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isSpanish } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Tutorial state
  const { isOpen: tutorialOpen, openWalkthrough, closeWalkthrough } = useConsumerWalkthrough();
  
  // Get token from localStorage safely (client-side only)
  const [token, setToken] = useState(null);
  
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    setToken(savedToken);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/consumer/dashboard', icon: Home, label: t('consumer.nav.dashboard', isSpanish ? 'Panel' : 'Dashboard'), testId: 'nav-home' },
    { path: '/consumer/browse', icon: ShoppingBag, label: t('consumer.nav.browse', isSpanish ? 'Explorar' : 'Browse'), testId: 'nav-browse' },
    { path: '/consumer/radar', icon: Radar, label: t('consumer.nav.radar', isSpanish ? 'Radar' : 'Deal Radar'), testId: 'nav-radar' },
    { path: '/consumer/favorites', icon: Heart, label: t('consumer.nav.favorites', isSpanish ? 'Favoritos' : 'Favorites'), testId: 'nav-favorites' },
    { path: '/consumer/retailers', icon: Store, label: t('consumer.nav.retailers', isSpanish ? 'Tiendas' : 'Retailers'), testId: 'nav-retailers' },
    { path: '/consumer/notifications', icon: Bell, label: t('consumer.nav.notifications', isSpanish ? 'Alertas' : 'Notifications'), testId: 'nav-notifications' },
    { path: '/consumer/orders', icon: Receipt, label: t('consumer.nav.orders', isSpanish ? 'Pedidos' : 'Orders'), testId: 'nav-orders' },
    { path: '/consumer/settings', icon: Settings, label: t('consumer.nav.settings', isSpanish ? 'Config.' : 'Settings'), testId: 'nav-settings' },
  ];

  const handleTutorialClick = () => {
    setMobileMenuOpen(false);
    openWalkthrough();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Email Verification Banner */}
      <EmailVerificationBanner user={user} />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-emerald-600" />
            </div>
            <div 
              className="cursor-pointer"
              onClick={() => navigate('/consumer/dashboard')}
            >
              <Logo size="default" />
              <p className="text-xs text-gray-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Language Switcher */}
            <LanguageSwitcher variant="compact" />
            
            {/* Order Updates Notification Bell */}
            <OrderNotificationBell />
            
            {/* Real-time Deal Notification Bell */}
            <NotificationBell token={token} />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="logout-btn"
              className="text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('common.logout', 'Logout')}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation - Desktop (horizontal) */}
      <nav className="bg-white border-b hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-emerald-600 text-emerald-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
            {/* Tutorial Button - Desktop */}
            <button
              data-testid="nav-tutorial"
              onClick={handleTutorialClick}
              className="flex items-center space-x-2 px-4 py-3 border-b-2 border-transparent text-purple-600 hover:text-purple-700 hover:border-purple-300 transition-colors whitespace-nowrap"
            >
              <HelpCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">{t('consumer.nav.tutorial', 'Tutorial')}</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Navigation - Mobile (dropdown) */}
      <nav className="bg-white border-b md:hidden">
        <div className="container mx-auto px-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-between w-full py-3 text-gray-700"
            data-testid="mobile-menu-toggle"
          >
            <div className="flex items-center space-x-2">
              <Menu className="w-5 h-5" />
              <span className="font-medium">
                {navItems.find(item => item.path === location.pathname)?.label || 'Menu'}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="absolute left-0 right-0 bg-white border-b shadow-lg z-40">
              <div className="container mx-auto px-4 py-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      data-testid={`mobile-${item.testId}`}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
                {/* Tutorial Button - Mobile */}
                <button
                  data-testid="mobile-nav-tutorial"
                  onClick={handleTutorialClick}
                  className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                >
                  <HelpCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{t('consumer.nav.tutorial', 'Tutorial')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">{children}</main>
      
      {/* Tutorial Walkthrough - Only shows when manually triggered */}
      <ConsumerWalkthrough 
        isOpen={tutorialOpen}
        onClose={closeWalkthrough}
        onComplete={closeWalkthrough}
      />
    </div>
  );
}
